/**
 * API Client
 * Module 07 - Dashboard UI
 * 
 * Axios instance with interceptors for authentication and error handling
 */

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

const getStoredToken = (): string | null => {
  const localToken = localStorage.getItem('token');
  if (localToken) return localToken;
  // Support session-based auth when VITE_AUTH_PERSISTENCE is not 'local'
  const sessionToken = sessionStorage.getItem('token');
  return sessionToken;
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.trim();
const useCookieAuth = import.meta.env.VITE_AUTH_USE_COOKIES === 'true';

const shouldUseProxy =
  import.meta.env.DEV &&
  (!API_BASE_URL || /^https?:\/\/(localhost|127\.0\.0\.1)/.test(API_BASE_URL));

const api = axios.create({
  // In development, prefer the Vite proxy to avoid CORS entirely.
  baseURL: shouldUseProxy ? '/api' : API_BASE_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: useCookieAuth,
});

/**
 * Request interceptor - add authentication token
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (!useCookieAuth) {
      const token = getStoredToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

let unauthorizedHandler: (() => void) | null = null;
let emailNotVerifiedHandler: (() => void) | null = null;

export const setUnauthorizedHandler = (handler: () => void) => {
  unauthorizedHandler = handler;
};

export const setEmailNotVerifiedHandler = (handler: () => void) => {
  emailNotVerifiedHandler = handler;
};

/**
 * Response interceptor - handle common errors
 * NOTE: 401 handling should be consumed by callers (AuthContext) to avoid hard reloads.
 */
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthRoute =
        url.includes('/auth/login') ||
        url.includes('/auth/register') ||
        url.includes('/auth/validate');
      if (unauthorizedHandler && !isAuthRoute) {
        unauthorizedHandler();
      }
      return Promise.reject(error);
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      const code = (error.response.data as { code?: string } | undefined)?.code;
      if (code === 'EMAIL_NOT_VERIFIED' && emailNotVerifiedHandler) {
        emailNotVerifiedHandler();
      }
      console.error('Access forbidden:', error.response.data);
    }
    
    // Handle 404 Not Found
    if (error.response?.status === 404) {
      console.error('Resource not found:', error.response.data);
    }
    
    // Handle 500 Internal Server Error
    if (error.response?.status === 500) {
      console.error('Server error:', error.response.data);
    }
    
    return Promise.reject(error);
  }
);

export default api;
